import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation} from 'react-router-dom';

/**
 * Regroupe les référentiels transverses (chauffeurs, camions, remorques, clients,
 * transitaires, armateurs) — auparavant des entrées séparées dans la liste plate de
 * Sidebar.tsx, chacune avec son propre droit. Gouverné désormais par un rôle unique,
 * MODULE_REGLAGES, vérifié en amont dans Sidebar.tsx — même patron que SidebarCarburantMenu/
 * SidebarPannesMenu : plus de filtrage par entrée ici, tous les items partagent le même accès.
 */

interface SidebarReglagesMenuProps {
    isCollapsed: boolean;
    /**
     * Gestion des Parcs a rejoint ce sous-menu mais garde son propre droit, MODULE_PARC, distinct
     * du MODULE_REGLAGES qui gouverne les autres entrées. Le filtrage reste donc décidé par
     * Sidebar.tsx, qui seul connaît l'utilisateur — mais entrée par entrée pour celle-ci, sans
     * quoi un utilisateur MODULE_REGLAGES sans MODULE_PARC verrait un lien menant à une page
     * interdite, et un utilisateur MODULE_PARC seul perdrait l'accès qu'il avait.
     */
    peutVoirParc?: boolean;
    /**
     * Les six référentiels, gouvernés par MODULE_REGLAGES. Explicite depuis que le menu peut
     * aussi s'ouvrir pour un utilisateur qui n'a que MODULE_PARC : sans ce drapeau, il verrait
     * des référentiels auxquels il n'a pas droit.
     */
    peutVoirReferentiels?: boolean;
}

const REFERENTIELS = [
    {id: 'chauffeurs', title: 'Gestion des Chauffeurs', icon: 'bi-person-badge', path: '/gestion-chauffeurs'},
    {id: 'camions', title: 'Gestion des Camions', icon: 'bi-truck', path: '/gestion-camions'},
    {id: 'remorques', title: 'Gestion des Remorques', icon: 'bi-truck-flatbed', path: '/gestion-remorques'},
    {id: 'clients', title: 'Gestion des Clients', icon: 'bi-people', path: '/gestion-clients'},
    {id: 'transitaires', title: 'Gestion des Transitaires', icon: 'bi-building', path: '/gestion-transitaires'},
    {id: 'armateurs', title: 'Armateurs', icon: 'bi-compass-fill', path: '/referentiels/armateurs'},
];

const PARC = {id: 'parcs', title: 'Gestion des Parcs', icon: 'bi-p-circle', path: '/gestion-parc'};

const SidebarReglagesMenu: React.FC<SidebarReglagesMenuProps> = ({
    isCollapsed,
    peutVoirParc = false,
    peutVoirReferentiels = true,
}) => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);

    const menuItems = [
        ...(peutVoirReferentiels ? REFERENTIELS : []),
        ...(peutVoirParc ? [PARC] : []),
    ];

    const isActive = (path: string) => location.pathname === path;
    const isAnyActive = () => menuItems.some(item => isActive(item.path));

    if (isCollapsed) {
        return (
            <div
                className="relative"
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setFlyoutPos({top: rect.top, left: rect.right + 8});
                    setIsExpanded(true);
                }}
                onMouseLeave={() => setIsExpanded(false)}
            >
                {isAnyActive() && (
                    <span
                        className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
                )}
                <div
                    className={`group py-3 mx-3 my-0.5 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        isAnyActive()
                            ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <i className={`bi bi-sliders text-[17px] transition-transform duration-200 ${isAnyActive() ? '' : 'group-hover:scale-110'}`}></i>
                </div>

                {isExpanded && flyoutPos && createPortal(
                    <div
                        style={{position: 'fixed', top: flyoutPos.top, left: flyoutPos.left}}
                        onMouseEnter={() => setIsExpanded(true)}
                        onMouseLeave={() => setIsExpanded(false)}
                        className="w-60 rounded-xl bg-green-900/95 backdrop-blur-sm ring-1 ring-white/10 shadow-2xl shadow-black/40 py-2 z-[1100] animate-[fadeIn_0.15s_ease-out]">
                        <p className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/70">Réglages</p>

                        {menuItems.map((item) => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className={`flex items-center py-2 px-4 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                    isActive(item.path)
                                        ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                        : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                                }`}
                            >
                                <i className={`bi ${item.icon} mr-2 text-sm`}></i>
                                <span className="flex-1">{item.title}</span>
                            </Link>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    return (
        <div className="mb-1.5 relative">
            {isAnyActive() && (
                <span
                    className="absolute left-1.5 top-4 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
            )}

            <div
                className={`group py-2.5 px-4 mx-3 my-0.5 rounded-xl cursor-pointer flex items-center justify-between transition-all duration-200 ${
                    isAnyActive()
                        ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center">
                    <i className={`bi bi-sliders text-[17px] mr-3 transition-transform duration-200 ${isAnyActive() ? '' : 'group-hover:scale-110'}`}></i>
                    <span className="text-[13px] font-medium tracking-wide">Réglages</span>
                </div>
                <i
                    className={`bi bi-chevron-down text-xs transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                />
            </div>

            <div
                className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="mx-3 my-1 rounded-xl bg-black/15 ring-1 ring-white/5 backdrop-blur-sm py-2 space-y-0.5">
                    {menuItems.map((item) => (
                        <Link
                            key={item.id}
                            to={item.path}
                            className={`flex items-center py-2 px-3 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                isActive(item.path)
                                    ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                    : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                            }`}
                        >
                            <i className={`bi ${item.icon} mr-2 text-sm`}></i>
                            <span className="flex-1">{item.title}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SidebarReglagesMenu;
